/**
 * @fileoverview The tips' requests that are not advice: what the season
 * review's own asks have been (seen, done, put away), marking one, and
 * keeping a beta link on the account. Advice (the tips and notices) is the
 * Today brief's (todo.reads) and is marked through api/todo markRead.
 *
 * Where: TipsContext only.
 * API: GET /tips (only its seen rows are read: the season asks'),
 * POST /tips/:key/shown|done|dismissed, POST /tips/beta.
 * Watch out: the marks and the beta call swallow their failures on purpose:
 * an ask that is not remembered just shows once more.
 *
 * @module tips/api
 */

import { apiClient } from '@/shared/api/apiClient';
import type { TipMark, TipSeen } from './types';

/** What the backend says that the app still reads: what was seen of the season review's asks. */
export interface TipsResponse {
  /** What this gardener has seen, per key and scope. */
  seen: TipSeen[];
}

/**
 * What this gardener has seen of the asks kept with the tips' marks.
 *
 * @returns The seen rows (the season asks' among them).
 */
export async function getTips(): Promise<TipsResponse> {
  const response = await apiClient.get<TipsResponse>('/tips');
  return response.data;
}

/**
 * Marks one of the season review's asks shown, done or put away on the
 * account. Quiet on failure: an ask that is not remembered simply shows once more.
 *
 * @param key - The ask's key.
 * @param mark - What happened to it.
 * @param scope - The season's year.
 */
export async function markTip(key: string, mark: TipMark, scope: number): Promise<void> {
  try {
    await apiClient.post(`/tips/${encodeURIComponent(key)}/${mark}`, { scope });
  } catch { /* nothing for the gardener to do */ }
}

/** Keeps a ?beta=1 link on the account. Quiet on failure: the link counts again on the next visit that has it. */
export async function joinBeta(): Promise<void> {
  try {
    await apiClient.post('/tips/beta');
  } catch { /* the link counts again on the next visit that has it */ }
}
